import type { Source } from '@/data/fixtures';

/**
 * Maps a source to one of the viewer kinds the layered viewer can render.
 *
 * Decision order:
 *   1. Mock-shape `type` field (covers seeded mock data)
 *   2. File extension from `original_filename` (most precise - matches the
 *      backend's text-extraction allowlist)
 *   3. MIME category (fallback for files without a clean extension)
 *   4. Generic `unsupported` - the viewer renders a download CTA instead.
 */
export type ViewerKind =
  | 'pdf'
  | 'image'
  | 'video'
  | 'audio'
  | 'link'
  | 'text' // Pasted-text source (content lives on the Source row)
  | 'markdown' // .md / .markdown
  | 'csv' // .csv / .tsv
  | 'html' // .html / .htm
  | 'plaintext' // .txt and other text-ish formats we can render via fetch + <pre>
  | 'docx' // .docx (lazy-load docx-preview)
  | 'unsupported';

const IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'tiff',
  'tif',
  'heic',
  'avif',
]);
const VIDEO_EXTS = new Set(['mp4', 'webm', 'ogg', 'mov', 'mkv']);
const AUDIO_EXTS = new Set(['mp3', 'wav', 'm4a', 'aac', 'flac']);
const PLAINTEXT_EXTS = new Set([
  'txt',
  'log',
  'json',
  'jsonl',
  'yaml',
  'yml',
  'toml',
  'ini',
  'env',
  'rst',
  'org',
]);

function getExt(source: Source): string | null {
  const name = source.original_filename || source.name || '';
  const ext = name.split('.').pop()?.toLowerCase();
  if (!ext || ext.length > 8 || !/^[a-z0-9]+$/.test(ext)) return null;
  return ext;
}

export function pickViewer(source: Source): ViewerKind {
  // Mock-data short-circuit: the seeded learning fixture uses these
  // coarse types and bypasses the API path entirely.
  if (source.type === 'link') return 'link';
  if (source.type === 'video') return 'video';
  // Don't return early on `'text'` - for API-mode the coarse type is
  // 'text' for pasted text *and* used as a fallback when mime fell
  // through, so we only treat it as a real text source when there's
  // no extension/mime to refine the decision.

  const ext = getExt(source);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'md' || ext === 'markdown') return 'markdown';
  if (ext === 'csv' || ext === 'tsv') return 'csv';
  if (ext === 'html' || ext === 'htm') return 'html';
  if (ext === 'docx') return 'docx';
  if (ext && IMAGE_EXTS.has(ext)) return 'image';
  if (ext && VIDEO_EXTS.has(ext)) return 'video';
  if (ext && AUDIO_EXTS.has(ext)) return 'audio';
  if (ext && PLAINTEXT_EXTS.has(ext)) return 'plaintext';

  // Mime-type fallback for files without a clean extension (or when the
  // upload didn't include a filename, e.g. paste-as-text).
  const mime = source.mime_type || '';
  if (mime.startsWith('application/pdf')) return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'text/markdown') return 'markdown';
  if (mime === 'text/csv' || mime === 'text/tab-separated-values') return 'csv';
  if (mime === 'text/html') return 'html';
  if (mime.startsWith('text/')) return 'plaintext';
  if (
    mime ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
    return 'docx';

  // Pasted text source - content lives directly on the Source row and is
  // fetched from `/sources/{id}/text`. A genuine pasted-text source has no
  // uploaded file, so it has no `original_filename`. We must gate on that:
  // `normalizeSource` coerces any FILE with an unrecognized mime to
  // `type: 'text'`, so without this guard an unknown upload (e.g. a .zip)
  // would route here and 404 against the text endpoint instead of showing
  // the download CTA.
  if (source.type === 'text' && !source.original_filename) return 'text';

  return 'unsupported';
}
